/**
 * DOM-free helpers behind `components/play-flow/ReviewStep.tsx` — split out
 * for the same reason `dispatch-inspector-logic.ts`/`settings-page-logic.ts`
 * are (mini-apps/CLAUDE.md "no DOM harness" convention): `ReviewStep.tsx`
 * pulls in Glaze components whose modules crash at import time under
 * `bun:test` (no jsdom/happy-dom registered under `mini-apps/`), so a pure
 * function that needs to be unit-tested has to live somewhere with zero
 * Glaze/React runtime imports, so this file is safe to import directly from
 * `bun:test`.
 */
import { MiniAppFetchError } from '../_shared/fetch-error';
import { sendPeopleLabel } from './send-summary';
import type { CampaignMember } from '../_shared/launcher-campaign.types';
import {
  PLAYBOOKS_TEST_SEND_MAX_MEMBERS,
  type PlaybooksMatchedMembersRung,
  type PlaybooksSendPlayResponse,
} from './schemas';

/**
 * The non-error subset of Glaze's `ToastStatus` (`'error' | 'info' |
 * 'success'`), spelled out rather than imported: `tsconfig.tests.json`'s
 * module resolution cannot resolve `@clay-run/glaze/toast/toast`.
 */
type SendToastStatus = 'info' | 'success';

/**
 * The post-send toast (plan 2026-10-14-002 U0b) — mode-aware so a rep can
 * never read a test toast as a real send, or vice versa.
 *
 * The send answers as soon as it is ACCEPTED: the roster is frozen and every
 * person already has a Queued row in the Dispatch log, but nothing has reached
 * Clay yet — building and starting happen in the background, at a pace. So
 * the copy says "started" / "sending" and points at the Dispatch log, where
 * each person's own outcome (sent, held back and why, stopped by Clay)
 * appears; it never claims anything was sent. A copy defect or a missing
 * sender is no longer a toast suffix: it is that person's own row.
 */
export function sendResultToast(
  response: PlaybooksSendPlayResponse,
  isTest: boolean,
): { content: string; status: SendToastStatus } {
  // A whole-play test over the cap picked a sample (plan 2026-10-15-005): say so, against what the play routes.
  const people = sendPeopleLabel(response.total, response.sampledFrom ?? null);
  return isTest
    ? { content: `Test started for ${people} — watch the Dispatch log below.`, status: 'success' }
    : { content: `Sending to ${people} — watch progress below.`, status: 'success' };
}

// ── What a rep reads when a send click goes wrong (plan 2026-10-14-002 U0a) ──

/**
 * The browser gave up waiting (core `apiFetch` aborts at its timeout). The
 * server may well have accepted the send — on 2026-10-14 it had, and the rep
 * pressed Send again and started a second full run — so this says the send may
 * be running and points at the Dispatch log, instead of reporting a failure.
 */
export const SEND_STILL_WORKING_SENTENCE =
  'Terra is still working on this send — check the Dispatch log below before pressing Send again.';

/** Anything the server did not explain in words (a server error, a dropped connection). */
export const SEND_FALLBACK_SENTENCE =
  "Couldn't send. Check the Dispatch log below before pressing Send again.";

export interface SendFailure {
  content: string;
  /** `'info'` when the send may be running; `'error'` when it was refused. */
  status: 'error' | 'info';
  /**
   * The server answered with a sentence that says it did NOT start (a 4xx with
   * a message). `false` for a timeout, a dropped connection or a 5xx — the
   * server may have accepted the send, so nothing may claim it "created nothing".
   */
  refused: boolean;
  /** The recipients or groups changed after the rep reviewed — offer "Reload and review again". */
  rosterChanged: boolean;
  /** A test of this play is already running — open its card. */
  testInFlight: boolean;
}

function isAbortLike(error: unknown): boolean {
  return (
    typeof error === 'object'
    && error !== null
    && 'name' in error
    && ((error as { name?: unknown }).name === 'AbortError' || (error as { name?: unknown }).name === 'TimeoutError')
  );
}

/**
 * The toast and follow-ups for a failed send (R20: never a raw status code,
 * a network string or "signal is aborted"):
 *   - an abort/timeout → "still working", as information;
 *   - a refusal the server explained (a 4xx `problem()` detail — a test already
 *     running, a test over the cap, an already-launched play, a roster that
 *     changed) → that sentence, as-is;
 *   - anything else (a 5xx, whose detail may be a raw error; a dropped
 *     connection; a non-error throw) → one plain sentence that sends the rep
 *     to the Dispatch log, because the server may have accepted the send.
 */
export function sendFailureOf(error: unknown): SendFailure {
  if (isAbortLike(error)) {
    return { content: SEND_STILL_WORKING_SENTENCE, status: 'info', refused: false, rosterChanged: false, testInFlight: false };
  }
  if (error instanceof MiniAppFetchError && error.status < 500 && error.userMessage) {
    return {
      content: error.userMessage,
      status: 'error',
      refused: true,
      rosterChanged: error.code === 'version_conflict',
      testInFlight: error.code === 'test_send_in_flight',
    };
  }
  return { content: SEND_FALLBACK_SENTENCE, status: 'error', refused: false, rosterChanged: false, testInFlight: false };
}

/** What the Review step is busy with — `'reload'` is "Reload and review again" (second review-fix pass U10). */
export type ReviewPendingAction = 'send' | 'apply' | 'reload' | null;

/**
 * Whether the approve buttons, "Approve all groups", Send and Apply are locked
 * (U10, C6-1). A reload replaces the roster the rep is approving, so approving
 * the groups still on screen during it would carry approvals over to people the
 * rep never reviewed — locked for every pending action, not just a send.
 */
export function reviewActionsLocked(pendingAction: ReviewPendingAction): boolean {
  return pendingAction !== null;
}

// ── The people under a Review folder (plan 2026-10-15-003 U3, §4.1–4.2) ──

/**
 * The one sentence for "the ladder is not usable right now" — shared by the
 * Review header tally and every folder body so they cannot drift. Takes the
 * two flags of the ladder query's state (`isError` wins, then `isPending`;
 * neither means the answer is version-stale).
 */
export function tallyUnavailableSentence(state: { isError: boolean; isPending: boolean }): string {
  if (state.isError) return "Couldn't match members to these groups. Press Re-check to try again.";
  if (state.isPending) return 'Matching members to each group…';
  return 'These groups changed since the last match — press Re-check for fresh counts.';
}

export interface ReviewRungMemberRows {
  /** Roster rows, in the ladder's order. */
  rows: CampaignMember[];
  /** Ladder ids with no roster row — reported, never silently dropped. */
  missingCount: number;
}

/**
 * Joins a folder's matched member ids (a ladder entry, or a bare id list for
 * "Not in any group") to the loaded roster, keeping the ladder's order.
 */
export function reviewRungMembers(
  source: Pick<PlaybooksMatchedMembersRung, 'matchedMemberIds'> | readonly string[] | null,
  members: readonly CampaignMember[],
): ReviewRungMemberRows {
  if (source === null) return { rows: [], missingCount: 0 };
  const ids = 'matchedMemberIds' in source ? source.matchedMemberIds : source;
  const byId = new Map(members.map((member) => [member.id, member]));
  const rows: CampaignMember[] = [];
  let missingCount = 0;
  for (const id of ids) {
    const row = byId.get(id);
    if (row) rows.push(row);
    else missingCount += 1;
  }
  return { rows, missingCount };
}

/** Client-side "Find a person": case-insensitive substring on name or email (Contact email, else Lead email). */
export function filterMembersByQuery(rows: readonly CampaignMember[], query: string): CampaignMember[] {
  const needle = query.trim().toLowerCase();
  if (needle === '') return [...rows];
  return rows.filter((member) =>
    [member.contact_name, member.contact_email, member.lead_email].some(
      (value) => typeof value === 'string' && value.toLowerCase().includes(needle),
    ),
  );
}

// ── A whole-play test of more than 100 people picks a sample (plan 2026-10-15-005) ──

/**
 * The sentence under the Test send / Real send toggle: how many people a test
 * builds messages for. `null` when nobody is routed (the caller shows nothing —
 * never a guessed number). Rulebook: sharing rules (s).
 */
export function testSendScopeSentence(
  routedCount: number,
  cap: number = PLAYBOOKS_TEST_SEND_MAX_MEMBERS,
): string | null {
  if (routedCount <= 0) return null;
  const routed = routedCount.toLocaleString('en-US');
  if (routedCount > cap) {
    return `A test builds messages for a sample of ${cap.toLocaleString('en-US')} of the ${routed} people this play routes — one person from each group first — and sends nothing.`;
  }
  const everyone = routedCount === 1 ? 'the 1 person' : `all ${routed} people`;
  return `A test builds messages for ${everyone} this play routes and sends nothing.`;
}

export type SendMode = 'test' | 'real';

/** `1:52 PM` — the same shape `formatSentAt` uses for its time. */
function formatClockTime(at: Date): string {
  return at.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export interface SendFailureInline {
  heading: string;
  content: string;
  status: 'error' | 'info';
  /** The Dispatch log's note — `null` unless the server refused (a send that may have been accepted created "nothing" is a lie). */
  note: string | null;
}

/**
 * The message that STAYS above the Send button after a failed press. `mode` is
 * the one captured at click time (never the live toggle); `content` is the
 * server's sentence verbatim. Only a REFUSED failure says "not started" and
 * leaves the Dispatch log note; every other failure (timeout, dropped
 * connection, 5xx) reads "may still be running", as information.
 */
export function sendFailureInline(failure: SendFailure, mode: SendMode, at: Date): SendFailureInline {
  const time = formatClockTime(at);
  if (!failure.refused) {
    return { heading: `Send may still be running (${time})`, content: failure.content, status: 'info', note: null };
  }
  return {
    heading: `${mode === 'test' ? 'Test send' : 'Send'} not started (${time})`,
    content: failure.content,
    status: 'error',
    note: lastRefusalNote(mode),
  };
}

/** The Dispatch log's note while a refusal message is up — it would otherwise read as "you never pressed anything". */
export function lastRefusalNote(mode: SendMode): string {
  return `The last ${mode === 'test' ? 'test' : 'Send'} created nothing — the reason is above the Send button.`;
}

// ── A play that has already had a real send (plan 2026-10-15-005 addendum) ──

/** Under the disabled "Send enrollments" of a play the server has already marked sent. */
export const ALREADY_SENT_NOTE = 'This play has already been sent. Duplicate the play to send it again.';

/**
 * Whether the play has had a real send — the server's own `launched` state
 * (set in the send's own transaction the moment a real send is accepted, so a
 * send still in progress counts; `versioning.ts#sendPlay` refuses a second one).
 * The UI follows that same state, never the toggle or the sends list.
 */
export function isPlaySent(play: { status: string } | null): boolean {
  return play?.status === 'launched';
}
